import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Container from "@material-ui/core/Container";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RedirectKeepUTM } from "../../../shared/components/RedirectKeepUTM";
import { useHistory } from "../../../shared/hooks/useHistory";
import { useSearchParams } from "../../../shared/hooks/useSearchParams";
import { validatePassword } from "../../../shared/validatePassword";
import { RootState } from "../../../store";
import { setLoginData } from "../../actions/login.actions";
import { setLogOut } from "../../actions/logout.actions";
import {
  createPasswordService,
  validateCreatePasswordService,
} from "../../services";
import { LoginLocationState } from "../loginPage/loginPage";

const initialFormState = {
  newPassword: "",
  confirmPassword: "",
};

export const CreateNewPasswordPage = (): JSX.Element => {
  const query = useSearchParams();
  const history = useHistory<LoginLocationState>();
  const dispatch = useDispatch();
  const [isFething, setIsFetching] = useState(true);
  const [wasForceLogout, setWasForceLogout] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isIdValid, setIsIdValid] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [formValues, setFormValues] = useState(initialFormState);
  const [formErrors, setFormErrors] = useState(initialFormState);
  const [username, setUsername] = useState("");
  const authId = useSelector<RootState>((state) => state.auth.login.authId);

  const queryId = query.get("id");

  useEffect(() => {
    dispatch(setLogOut());
    setWasForceLogout(true);
  }, [dispatch]);

  useEffect(() => {
    if (wasForceLogout && authId) {
      history.push(`/participant`, {
        isCreatePasswordSuccess: true,
      });
    }
  }, [history, authId, queryId, wasForceLogout]);

  useEffect(() => {
    const fetch = async () => {
      try {
        const response = await validateCreatePasswordService({ id: queryId });
        if (response.status === 200) {
          if (response.data.data.isCompleted) {
            setIsIdValid(false);
            return;
          }
          setUsername(response.data.data.userName);
          setIsIdValid(true);
          return;
        }
      } catch (e) {
        setIsIdValid(false);
      } finally {
        setIsFetching(false);
      }
    };

    fetch();
  }, [queryId]);

  const getIsDisabled = () => {
    const { newPassword, confirmPassword } = formErrors;

    return isUpdating || !!(newPassword.length || confirmPassword.length);
  };

  const handleBlur = (e) => {
    const { value, name } = e.target;

    if (!value) {
      setFormErrors({ ...formErrors, [name]: "This field is required" });
      return;
    }

    const validationResult = validatePassword(value);
    if (!validationResult.isValid) {
      setFormErrors({
        ...formErrors,
        [name]:
          "Must include 8 characters, 1 number, 1 special character and 1 alphabetic value at least",
      });
      return;
    }

    if (name === "confirmPassword" && value !== formValues.newPassword) {
      setFormErrors({
        ...formErrors,
        [name]: "The password doesn't match",
      });
      return;
    }

    setFormErrors({
      ...formErrors,
      [name]: "",
    });
  };

  const handleChange = ({ target: { name, value } }) =>
    setFormValues({ ...formValues, [name]: value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsUpdating(true);
    const response = await createPasswordService({
      participantProfileId: queryId,
      password: formValues.newPassword,
      confirmPassword: formValues.confirmPassword,
    });

    setIsUpdating(false);

    if (response.status !== 200) {
      const message = response.data.errors.length
        ? response.data.errors.toString()
        : response.data.warnings.toString();
      if (formValues.newPassword === "") {
        setErrorMessage("Password field is required");
      } else if (formValues.confirmPassword === "") {
        setErrorMessage("Confirm password field is required");
      } else {
        setErrorMessage(message);
      }
      return;
    }

    dispatch(setLoginData(response.data));
  };

  const handleCancel = () => {
    setFormValues(initialFormState);
    setFormErrors(initialFormState);
    setErrorMessage("");
  };

  if (isFething) {
    return <span>Loading...</span>;
  }

  if (!queryId || !isIdValid) {
    return (
      <RedirectKeepUTM
        to={{
          pathname: "/",
          state: { isCreatePasswordExpired: !isIdValid },
        }}
      />
    );
  }

  return (
    <Container component="main" maxWidth="xs">
      <Grid container spacing={4} direction="column">
        <Box mt={4} />
        <Grid item>
          <Typography component="h1" variant="h5" align="center">
            Create New Password
          </Typography>
        </Grid>

        <Grid item>
          <Typography align="center">Username {username}</Typography>
        </Grid>

        <Grid item>
          {errorMessage && (
            <Typography align="center">{errorMessage}</Typography>
          )}
        </Grid>

        <Grid item>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={4} direction="column">
              <Grid item>
                <TextField
                  type="password"
                  name="newPassword"
                  label="New password"
                  id="newPassword"
                  fullWidth
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={formValues.newPassword}
                  error={!!formErrors.newPassword.length}
                  helperText={formErrors.newPassword}
                />
              </Grid>

              <Grid item>
                <TextField
                  type="password"
                  name="confirmPassword"
                  label="Confirm password"
                  id="confirmPassword"
                  fullWidth
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={formValues.confirmPassword}
                  error={!!formErrors.confirmPassword.length}
                  helperText={formErrors.confirmPassword}
                />
              </Grid>

              <Grid item>
                <Button
                  color="primary"
                  fullWidth
                  variant="contained"
                  type="submit"
                  disabled={getIsDisabled()}
                >
                  Create password
                </Button>
              </Grid>

              <Grid item>
                <Button
                  color="secondary"
                  variant="contained"
                  fullWidth
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
              </Grid>
            </Grid>
          </form>
        </Grid>
      </Grid>
    </Container>
  );
};
